import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Enum arrays for type-safe filtering
export const sourceTypes = [
  "campaign",
  "automation",
  "marketing_agent",
  "unknown",
] as const;
export type SourceType = (typeof sourceTypes)[number];

export const channelTypes = ["email", "sms"] as const;
export type ChannelType = (typeof channelTypes)[number];

// Input schema
export const GetCustomerMessageTimelineInputSchema = z.object({
  customerId: z.string().describe("Customer ID to fetch messages for"),
  // Filter 1: Source
  sourceTypes: z
    .array(z.enum(["campaign", "automation", "marketing_agent"]))
    .optional()
    .describe("Filter by message source"),
  // Filter 2: AI Generated
  isAiGenerated: z
    .boolean()
    .optional()
    .describe("Filter by AI-generated messages"),
  // Filter 3: Category
  categories: z
    .array(z.string())
    .optional()
    .describe("Filter by message categories"),
  // Filter 4: Reply vs Outreach
  isReply: z.boolean().optional().describe("Filter by reply/outreach status"),
  // Filter 5: Channel
  channel: z
    .enum(MarketingChannel)
    .optional()
    .describe("Filter by channel type"),
  // Filter 6: Date Range
  startDate: z
    .string()
    .optional()
    .describe("Start date for the filter period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .optional()
    .describe("End date for the filter period (YYYY-MM-DD)"),
  // Pagination
  page: z.number().positive().default(1).describe("Page number"),
  pageSize: z
    .number()
    .positive()
    .max(100)
    .default(20)
    .describe("Number of results per page"),
});

// Message row schema
const MessageTimelineRowSchema_ = z.object({
  eventId: z.string(),
  teamId: z.string(),
  customerId: z.string(),
  channel: createEnum(MarketingChannel),
  channelId: z.string(),
  category: z.string().nullable(),
  timestamp: z.coerce.date(),
  sourceType: z.enum(sourceTypes),
  isAiGenerated: z.boolean(),
  isReply: z.boolean(),
  campaignId: z.string().nullable(),
  automationId: z.string().nullable(),
  marketingMessageId: z.string().nullable(),
  contentText: z
    .string()
    .nullable()
    .describe("SMS/MMS message text (NULL for email)"),
  contentUrl: z
    .string()
    .nullable()
    .describe("S3 URL for email content (NULL for SMS)"),
});
export const MessageTimelineRowSchema: z.ZodType<MessageTimelineRow> =
  MessageTimelineRowSchema_;
export interface MessageTimelineRow
  extends z.infer<typeof MessageTimelineRowSchema_> {}

// Output schema
export const GetCustomerMessageTimelineOutputSchema = z.object({
  messages: z.array(MessageTimelineRowSchema),
  totalCount: z.number().describe("Total number of messages matching filters"),
});

// Export types
export type GetCustomerMessageTimelineInput = z.infer<
  typeof GetCustomerMessageTimelineInputSchema
>;
export type GetCustomerMessageTimelineOutput = z.infer<
  typeof GetCustomerMessageTimelineOutputSchema
>;

// Export RPC schema definition
export const getCustomerMessageTimeline = {
  name: "getCustomerMessageTimeline" as const,
  input: GetCustomerMessageTimelineInputSchema,
  output: GetCustomerMessageTimelineOutputSchema,
};
